import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Zap, Sparkles, Crown, Rocket, Star, TrendingUp, Award, Gift } from "lucide-react";

const PLAN_FEATURES = {
    pod: [
        "Ad-free browsing",
        "See who rated you + exact scores",
        "15 posts per day (free = 5)",
        "Basic profile badge",
        "Priority in Fresh feed",
    ],
    elite: [
        "Everything in Pod Member",
        "Unlimited posts",
        "Higher-res uploads (25MB)",
        "Advanced filters & search",
        "Verified badge",
        "Early access to new features",
        "Profile analytics",
        "1 free Boost per month",
    ],
};

const BOOST_ICONS = {
    post_boost: Zap,
    premium_boost: Rocket,
    profile_feature: Star,
    weekly_spotlight: TrendingUp,
    boost_pack_5: Gift,
    custom_badge: Award,
};

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [interval, setIntervalKey] = useState("year");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/payments/catalog").then((r) => setCatalog(r.data)).catch((e) => toast.error(formatApiError(e)));
    }, []);

    const checkout = async (lookup_key) => {
        if (!user) { navigate("/login"); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/payments/checkout", {
                lookup_key,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    if (!catalog) return <main className="max-w-5xl mx-auto p-8 font-bold text-[#4A4A4A]">Loading pricing…</main>;

    const podPlan = catalog.plans.find((p) => p.tier === "pod" && p.interval === interval);
    const elitePlan = catalog.plans.find((p) => p.tier === "elite" && p.interval === interval);
    const currentTier = user?.tier || "free";

    return (
        <main className="max-w-5xl mx-auto px-4 py-8 sm:py-12">
            <div className="text-center mb-10">
                <div className="inline-block bg-[#FFE55C] border-2 border-[#0A0A0A] px-3 py-1 rounded-full text-xs font-black tracking-wider uppercase mb-3">
                    Pricing
                </div>
                <h1 className="display text-4xl sm:text-6xl font-black leading-[0.95] mb-3" data-testid="pricing-title">
                    Level up your <span className="bg-[#FFB8D1] px-2 border-2 border-[#0A0A0A] rounded-lg inline-block">soles</span>.
                </h1>
                <p className="text-base sm:text-lg text-[#4A4A4A] font-medium max-w-xl mx-auto">
                    Free forever. Upgrade to see who's rating you, skip ads, and post more.
                </p>
                <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full brut-inset bg-[#FFE55C] text-xs font-bold" data-testid="promo-hint">
                    <span className="text-[10px] uppercase tracking-widest font-black">Launch offer</span>
                    <span>Code <span className="serif italic font-normal">FOUNDING</span> = 20% off Pod Member yearly, forever</span>
                </div>

                <div className="mt-6 inline-flex items-center gap-1 p-1 bg-white brut-static rounded-full" data-testid="interval-toggle">
                    <button
                        onClick={() => setIntervalKey("month")}
                        className={`px-4 py-2 rounded-full text-sm font-bold ${interval === "month" ? "bg-[#0A0A0A] text-white" : ""}`}
                        data-testid="interval-month"
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setIntervalKey("year")}
                        className={`px-4 py-2 rounded-full text-sm font-bold ${interval === "year" ? "bg-[#0A0A0A] text-white" : ""}`}
                        data-testid="interval-year"
                    >
                        Yearly <span className="ml-1 text-[#FFE55C]">save 33%</span>
                    </button>
                </div>
            </div>

            <section className="grid md:grid-cols-3 gap-6 mb-16">
                <PlanCard
                    tier="free"
                    name="Free"
                    tagline="Rate. Post. Repeat."
                    price={0}
                    interval={interval}
                    features={["Create profile & post photos", "Rate any post 1-10", "Browse Fresh & Top rated", "5 posts per day", "Ads shown"]}
                    cta={currentTier === "free" ? "Current plan" : "Free tier"}
                    disabled
                    color="#FFFFFF"
                    testId="plan-free"
                />
                <PlanCard
                    tier="pod"
                    name="Pod Member"
                    tagline="See your scores. Skip the ads."
                    price={podPlan.price}
                    interval={interval}
                    features={PLAN_FEATURES.pod}
                    cta={currentTier === "pod" ? "Current plan" : "Get Pod Member"}
                    disabled={currentTier === "pod" || loading}
                    onClick={() => checkout(podPlan.lookup_key)}
                    color="#FFB8D1"
                    icon={Sparkles}
                    highlight
                    testId="plan-pod"
                />
                <PlanCard
                    tier="elite"
                    name="Sole Elite"
                    tagline="Unlimited angles. Full stats."
                    price={elitePlan.price}
                    interval={interval}
                    features={PLAN_FEATURES.elite}
                    cta={currentTier === "elite" ? "Current plan" : "Go Elite"}
                    disabled={currentTier === "elite" || loading}
                    onClick={() => checkout(elitePlan.lookup_key)}
                    color="#FFE55C"
                    icon={Crown}
                    testId="plan-elite"
                />
            </section>

            <section>
                <div className="flex items-baseline justify-between mb-5">
                    <h2 className="display text-3xl font-black">Boosts & extras</h2>
                    <div className="text-xs font-bold uppercase text-[#4A4A4A] tracking-wider">One-time purchases</div>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {catalog.boosts.map((b) => {
                        const Icon = BOOST_ICONS[b.lookup_key] || Zap;
                        // Skip post-target boosts here — those live on post cards
                        if (["post_boost", "premium_boost"].includes(b.lookup_key)) return (
                            <div key={b.lookup_key} className="bg-white brut-static rounded-xl p-5" data-testid={`boost-${b.lookup_key}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-9 h-9 rounded-lg bg-[#B8E1FF] border-2 border-[#0A0A0A] flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                                    <div className="font-black">{b.name}</div>
                                </div>
                                <div className="text-sm font-medium text-[#4A4A4A] mb-3 min-h-[40px]">{b.desc}</div>
                                <div className="flex items-center justify-between">
                                    <div className="display text-2xl font-black">${b.price}</div>
                                    <div className="text-xs text-[#4A4A4A] font-bold uppercase">Buy from any post</div>
                                </div>
                            </div>
                        );
                        return (
                            <div key={b.lookup_key} className="bg-white brut-static rounded-xl p-5" data-testid={`boost-${b.lookup_key}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="w-9 h-9 rounded-lg bg-[#B8E1FF] border-2 border-[#0A0A0A] flex items-center justify-center"><Icon className="w-4 h-4" /></div>
                                    <div className="font-black">{b.name}</div>
                                </div>
                                <div className="text-sm font-medium text-[#4A4A4A] mb-3 min-h-[40px]">{b.desc}</div>
                                <div className="flex items-center justify-between">
                                    <div className="display text-2xl font-black">${b.price}</div>
                                    <button
                                        onClick={() => checkout(b.lookup_key)}
                                        disabled={loading}
                                        className="px-3 py-2 rounded-lg brut bg-[#FFE55C] font-bold text-sm"
                                        data-testid={`buy-${b.lookup_key}`}
                                    >
                                        Buy
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className="text-xs text-[#4A4A4A] font-medium mt-6 text-center max-w-2xl mx-auto">
                    Tips include a 15% platform fee. All payments processed by Stripe. Cancel subscriptions anytime — you keep access until the end of the billing period.
                </p>
            </section>
        </main>
    );
}

function PlanCard({ tier, name, tagline, price, interval, features, cta, disabled, onClick, color, icon: Icon, highlight, testId }) {
    return (
        <div
            className={`relative bg-white rounded-xl p-6 ${highlight ? "brut-lg" : "brut-static"}`}
            data-testid={testId}
            style={{ backgroundColor: color }}
        >
            {highlight && (
                <div className="absolute -top-3 left-6 bg-[#0A0A0A] text-[#FFE55C] px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider">
                    Most popular
                </div>
            )}
            <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon className="w-5 h-5" />}
                <h3 className="display text-2xl font-black">{name}</h3>
            </div>
            <div className="text-sm font-medium text-[#0A0A0A]/70 mb-4">{tagline}</div>
            <div className="flex items-baseline gap-1 mb-5">
                <div className="display text-5xl font-black">${price.toFixed(2)}</div>
                <div className="text-sm font-bold text-[#0A0A0A]/70">/{interval === "year" ? "yr" : "mo"}</div>
            </div>
            <ul className="space-y-2 mb-6 min-h-[240px]">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm font-medium">
                        <Check className="w-4 h-4 mt-0.5 shrink-0" /> <span>{f}</span>
                    </li>
                ))}
            </ul>
            <button
                onClick={onClick}
                disabled={disabled}
                className={`w-full px-4 py-3 rounded-lg font-bold ${disabled ? "brut-inset bg-white/50 opacity-70" : "brut bg-[#0A0A0A] text-white"}`}
                data-testid={`${testId}-cta`}
            >
                {cta}
            </button>
        </div>
    );
}
